import { Container } from '@cleartrip/ct-design-components'
import { AnimatePresence, motion } from 'framer-motion'
import { isValidElement, memo, type ReactNode, type RefObject, useEffect, useRef } from 'react'

import type { TupleGroupTheme } from '../config/tupleTheme'

/** Scroll group into view before collapsing when the shell sits above the viewport. */
export const scrollAwareCollapse = (
  el: HTMLElement | null,
  onCollapse: () => void,
  timerRef?: React.MutableRefObject<NodeJS.Timeout | null>
) => {
  if (el) {
    const rect = el.getBoundingClientRect()
    if (rect.top < -20) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      const timer = setTimeout(onCollapse, 250)
      if (timerRef) timerRef.current = timer
      return
    }
  }
  onCollapse()
}

export interface ExpandableTupleGroupProps {
  theme: TupleGroupTheme
  expanded: boolean
  header?: ReactNode
  collapsedFooter?: ReactNode
  expandedFooter?: ReactNode
  children: ReactNode
  siblings?: ReactNode
  /** When false and collapsed, render children without the coloured shell. */
  active?: boolean
  watermark?: ReactNode
  groupRef?: RefObject<HTMLDivElement | null>
}

const ExpandableTupleGroup: React.FC<ExpandableTupleGroupProps> = ({
  theme,
  expanded,
  header = null,
  collapsedFooter = null,
  expandedFooter = null,
  children,
  siblings = null,
  active = true,
  watermark = null,
  groupRef,
}) => {
  const localRef = useRef<HTMLDivElement>(null)
  const containerRef = groupRef ?? localRef
  const lastSiblingsRef = useRef<ReactNode>(siblings)

  // Keep last siblings mounted during exit animation.
  if (expanded && siblings && (Array.isArray(siblings) ? siblings.length > 0 : true)) {
    lastSiblingsRef.current = siblings
  }

  const renderedSiblings = expanded ? siblings : lastSiblingsRef.current

  useEffect(() => {
    if (expanded && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect()
      if (rect.top < -20) {
        containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }
  }, [expanded, containerRef])

  if (!active && !expanded) {
    return <>{children}</>
  }

  const pad = expanded ? theme.expandedPadding : theme.collapsedPadding
  // Footer owns bottom chrome when present — avoid stacking shell pb + footer py.
  const paddingBottom = !expanded && collapsedFooter ? 0 : pad.bottom

  return (
    <motion.div
      ref={containerRef as React.Ref<HTMLDivElement>}
      animate={{
        paddingTop: pad.top,
        paddingBottom,
        paddingLeft: pad.left,
        paddingRight: pad.right,
        borderColor: expanded ? theme.expandedBorderColor : theme.borderColor,
        boxShadow: expanded ? theme.expandedShadow : '0px 0px 0px 0px rgba(0, 0, 0, 0)',
      }}
      className='br-16 o-hidden w-100p p-relative'
      initial={false}
      style={{
        background: theme.shellBg,
        borderStyle: 'solid',
        borderWidth: 1,
        scrollMarginTop: 16,
      }}
      transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
    >
      {watermark}
      {header}

      <Container className='flex flex-column'>
        {children}

        <AnimatePresence initial={false}>
          {expanded && renderedSiblings && (
            <motion.div
              key='tuple-group-siblings'
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              initial={{ opacity: 0, height: 0 }}
              style={{ overflow: 'hidden' }}
              transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
            >
              <Container className='flex flex-column flex-rg-3 pt-3'>
                {Array.isArray(renderedSiblings)
                  ? renderedSiblings.map((sibling, idx) => {
                      const key = isValidElement(sibling) && sibling.key ? sibling.key : `tuple-group-sibling-${idx}`
                      return (
                        <motion.div
                          key={key}
                          animate={{ opacity: 1, y: 0 }}
                          initial={{ opacity: 0, y: -8 }}
                          transition={{ duration: 0.25, delay: idx * 0.04 }}
                        >
                          {sibling}
                        </motion.div>
                      )
                    })
                  : renderedSiblings}
              </Container>
            </motion.div>
          )}
        </AnimatePresence>
      </Container>

      <AnimatePresence initial={false}>
        {expanded ? (
          expandedFooter ? (
            <motion.div
              key='tuple-group-hide-footer'
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              initial={{ opacity: 0, height: 0 }}
              style={{ overflow: 'hidden' }}
              transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
            >
              {expandedFooter}
            </motion.div>
          ) : null
        ) : collapsedFooter ? (
          <motion.div
            key='tuple-group-nudge'
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            initial={{ opacity: 0, height: 0 }}
            style={{ overflow: 'hidden' }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
          >
            {collapsedFooter}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </motion.div>
  )
}

export default memo(ExpandableTupleGroup)
